import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockOutlined from "@mui/icons-material/LockOutlined";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import Button from "../../Button";
import TextField from "../../TextField";
import PasswordField from "../../PasswordField";
import { useCookies } from "react-cookie";
import TabHeader from "../../TabHeader";
import { useLocation, useNavigate } from "react-router-dom";
import Account from "../../../api/Account";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";

interface Props {
    type: "email" | "password";
}

const ChangeCredential = ({ type }: Props) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();
    const [cookies] = useCookies(["uuid"]);
    const { uuid } = cookies;

    const [currentValue, setCurrentValue] = useState("");
    const [newValue, setNewValue] = useState("");
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const isPartner = location.state?.isPartner ?? false;

    const isPassword = type === "password";

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    useEffect(() => {
        if (!uuid) {
            navigate("/");
            return;
        }
    }, [uuid, navigate]);

    const changeCredential = useCallback(() => {
        if (!uuid) return;
        if (currentValue === "" || newValue === "") {
            alert("Fill all the fields.");
            return;
        }
        if (type === "email" && currentValue !== newValue) {
            alert("Emails don't match");
            return;
        }
        setLoading(true);

        const apiCall =
            type === "password"
                ? () => Account.changePassword(uuid, currentValue, newValue)
                : () => Account.changeEmail(uuid, newValue);

        apiCall()
            .then(() => {
                navigate("/");
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, currentValue, newValue, navigate, type]);

    return (
        <>
            <TabHeader
                name={`Change ${isPassword ? "Password" : "Email"}`}
                onClick={() => navigate(isPartner ? "/partner" : "/")}
            />
            <Stack
                width="100%"
                flex={1}
                alignItems="center"
                justifyContent="center"
                spacing={3}
                px={2}
                sx={{
                    boxSizing: "border-box",
                    "@keyframes fadeSlideIn": {
                        from: { opacity: 0, transform: "translateY(12px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
            >
                {/* Icon + heading */}
                <Stack alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "16px",
                            bgcolor: alpha(primary, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isPassword ? (
                            <LockOutlined
                                sx={{ fontSize: 28, color: primary }}
                            />
                        ) : (
                            <EmailOutlined
                                sx={{ fontSize: 28, color: primary }}
                            />
                        )}
                    </Box>
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="18px"
                        fontWeight={700}
                        textAlign="center"
                    >
                        {isPassword
                            ? "Set a new password"
                            : "Update your email"}
                    </Typography>
                    <Typography
                        fontSize="13px"
                        color="text.secondary"
                        textAlign="center"
                        maxWidth="280px"
                    >
                        {isPassword
                            ? "Enter your current password and choose a new one."
                            : "Enter your new email address twice to confirm."}
                    </Typography>
                </Stack>

                {/* Form card */}
                <Stack sx={CARD_STYLES} spacing={2.5} width="100%">
                    <Stack spacing={0.75}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            <Box sx={iconBoxSx}>
                                {isPassword ? (
                                    <LockOutlined
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                ) : (
                                    <EmailOutlined
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                )}
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>
                                {isPassword ? "Current password" : "New email"}
                            </Typography>
                        </Stack>
                        {isPassword ? (
                            <PasswordField
                                variant="contained"
                                fullWidth
                                placeholder="Enter current password"
                                value={currentValue}
                                onChange={(e) =>
                                    setCurrentValue(e.currentTarget.value)
                                }
                            />
                        ) : (
                            <TextField
                                type="email"
                                variant="contained"
                                fullWidth
                                placeholder="Enter new email"
                                value={currentValue}
                                onChange={(e) =>
                                    setCurrentValue(e.currentTarget.value)
                                }
                            />
                        )}
                    </Stack>

                    <Stack spacing={0.75}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            <Box sx={iconBoxSx}>
                                {isPassword ? (
                                    <LockOutlined
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                ) : (
                                    <EmailOutlined
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                )}
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>
                                {isPassword
                                    ? "New password"
                                    : "Confirm new email"}
                            </Typography>
                        </Stack>
                        {isPassword ? (
                            <PasswordField
                                variant="contained"
                                fullWidth
                                placeholder="Enter new password"
                                value={newValue}
                                onChange={(e) =>
                                    setNewValue(e.currentTarget.value)
                                }
                            />
                        ) : (
                            <TextField
                                type="email"
                                variant="contained"
                                fullWidth
                                placeholder="Confirm new email"
                                value={newValue}
                                onChange={(e) =>
                                    setNewValue(e.currentTarget.value)
                                }
                            />
                        )}
                    </Stack>
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <Button
                        variant="big"
                        disabled={loading}
                        onClick={changeCredential}
                        color="primary"
                        fullWidth
                    >
                        Confirm
                    </Button>
                </Box>
            </Stack>
        </>
    );
};

export default ChangeCredential;
